import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions.js";
import { ROUTES } from "../../routes.js";
import styles from "./HomePage.module.css";
import { Tile } from "@legal-500/legal500-design-system";

type TileItem = {
  to: string;
  title: string;
  description: string;
  onClick: () => void;
};

type TileGroup = {
  label: string;
  visible: boolean;
  items: TileItem[];
};

function HomePage() {
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission } = usePermissions();

  const canSeeTaxonomy = hasPermission("taxonomy.read");
  const canSeeFulfilment = hasAnyPermission([
    "fulfilment.read_subscriptions",
    "fulfilment.read_packages",
    "fulfilment.read_features",
  ]);
  const canSeeFulfilmentFeatures = hasPermission("fulfilment.read_features");
  const canSeeFulfilmentPackages = hasPermission("fulfilment.read_packages");
  const canSeeFulfilmentSubscriptions = hasPermission("fulfilment.read_subscriptions");
  const canSeeFulfilmentCrm = hasPermission("fulfilment.read_subscriptions");
  const canSeeAddressBook = hasPermission("addressbook.read");
  const canSeeDataMarts = hasPermission("data-marts.read");
  const canSeeSubmissionsTab = hasAnyPermission(["staff.submission.read", "staff.referee.read"]);
  const canSeeSubmissionsList = hasPermission("staff.submission.read");
  const canSeeReferees = hasPermission("staff.referee.read");
  const canSeeResearchTab = hasAnyPermission(["staff.approval.read", "staff.research.read"]);
  const canSeeContentInbox = hasPermission("staff.approval.read");
  const canSeeResearchCms = hasPermission("staff.research.read");
  const canSeeDeveloper = hasAnyPermission(["developer.job_runs", "swagger:read"]);
  const canSeeSwagger = hasPermission("swagger:read");
  const canSeeJobRuns = hasPermission("developer.job_runs");

  const groups: TileGroup[] = [
    {
      label: "Research",
      visible: canSeeResearchTab,
      items: [
        ...(canSeeContentInbox
          ? [
              {
                to: ROUTES.research.contentInbox,
                title: "Testimonials",
                description: "Review and approve testimonial submissions",
                onClick: () => navigate(ROUTES.research.contentInbox),
              },
            ]
          : []),
        ...(canSeeResearchCms
          ? [
              {
                to: ROUTES.research.cms,
                title: "Research Portal",
                description: "Manage editions, practice areas and rankings",
                onClick: () => navigate(ROUTES.research.cms),
              },
            ]
          : []),
      ],
    },
    {
      label: "Taxonomy",
      visible: canSeeTaxonomy,
      items: [
        {
          to: ROUTES.taxonomy.regions,
          title: "Regions",
          description: "Manage geographical regions and jurisdictions",
          onClick: () => navigate(ROUTES.taxonomy.regions),
        },
        {
          to: ROUTES.taxonomy.directories,
          title: "Directories",
          description: "Manage publication directories and structures",
          onClick: () => navigate(ROUTES.taxonomy.directories),
        },
        {
          to: ROUTES.taxonomy.bpas,
          title: "Business Practice Areas",
          description: "Organize and manage business practice areas",
          onClick: () => navigate(ROUTES.taxonomy.bpas),
        },
        {
          to: ROUTES.taxonomy.editions,
          title: "Editions",
          description: "Control publication editions and versions",
          onClick: () => navigate(ROUTES.taxonomy.editions),
        },
        {
          to: ROUTES.taxonomy.miscellaneous,
          title: "Miscellaneous",
          description: "Manage miscellaneous taxonomy items",
          onClick: () => navigate(ROUTES.taxonomy.miscellaneous),
        },
      ],
    },
    {
      label: "Fulfilment",
      visible: canSeeFulfilment,
      items: [
        ...(canSeeFulfilmentFeatures
          ? [
              {
                to: ROUTES.fulfilment.features,
                title: "Features",
                description: "View and manage fulfilment features",
                onClick: () => navigate(ROUTES.fulfilment.features),
              },
            ]
          : []),
        ...(canSeeFulfilmentPackages
          ? [
              {
                to: ROUTES.fulfilment.packages,
                title: "Packages",
                description: "Manage subscription packages and bundles",
                onClick: () => navigate(ROUTES.fulfilment.packages),
              },
            ]
          : []),
        ...(canSeeFulfilmentSubscriptions
          ? [
              {
                to: ROUTES.fulfilment.subscriptions,
                title: "Subscriptions",
                description: "Manage customer subscriptions and entitlements",
                onClick: () => navigate(ROUTES.fulfilment.subscriptions),
              },
            ]
          : []),
        ...(canSeeFulfilmentCrm
          ? [
              {
                to: ROUTES.fulfilment.crmPackageMappings,
                title: "CRM Package Mappings",
                description: "Map CRM packages to platform subscriptions",
                onClick: () => navigate(ROUTES.fulfilment.crmPackageMappings),
              },
              {
                to: ROUTES.fulfilment.crmWebhookLogs,
                title: "CRM Webhook",
                description: "Monitor and manage CRM webhook events",
                onClick: () => navigate(ROUTES.fulfilment.crmWebhookLogs),
              },
            ]
          : []),
      ],
    },
    {
      label: "Address Book",
      visible: canSeeAddressBook,
      items: [
        {
          to: ROUTES.addressBook.companies,
          title: "Companies",
          description: "Manage law firm and company records",
          onClick: () => navigate(ROUTES.addressBook.companies),
        },
        {
          to: ROUTES.addressBook.contacts,
          title: "Contacts",
          description: "Manage individual contact records",
          onClick: () => navigate(ROUTES.addressBook.contacts),
        },
        {
          to: ROUTES.addressBook.offices,
          title: "Offices",
          description: "Manage office locations and details",
          onClick: () => navigate(ROUTES.addressBook.offices),
        },
      ],
    },
    {
      label: "Data Marts",
      visible: canSeeDataMarts,
      items: [
        {
          to: ROUTES.dataMarts.root,
          title: "Data Marts",
          description: "Access and manage data mart exports",
          onClick: () => navigate(ROUTES.dataMarts.root),
        },
      ],
    },
    {
      label: "Submissions",
      visible: canSeeSubmissionsTab,
      items: [
        ...(canSeeSubmissionsList
          ? [
              {
                to: ROUTES.submissions.list,
                title: "Submissions",
                description: "Review and manage firm submissions",
                onClick: () => navigate(ROUTES.submissions.list),
              },
            ]
          : []),
        ...(canSeeReferees
          ? [
              {
                to: ROUTES.submissions.referees,
                title: "Referees",
                description: "Manage referee information and feedback",
                onClick: () => navigate(ROUTES.submissions.referees),
              },
            ]
          : []),
      ],
    },
    {
      label: "Developer",
      visible: canSeeDeveloper,
      items: [
        ...(canSeeSwagger
          ? [
              {
                to: ROUTES.developer.swagger,
                title: "Swagger",
                description: "Browse API documentation and endpoints",
                onClick: () => navigate(ROUTES.developer.swagger),
              },
            ]
          : []),
        ...(canSeeJobRuns
          ? [
              {
                to: ROUTES.developer.syncers,
                title: "Syncers",
                description: "Manage data synchronization jobs",
                onClick: () => navigate(ROUTES.developer.syncers),
              },
            ]
          : []),
      ],
    },
  ];

  const visibleGroups = groups.filter(group => group.visible && group.items.length > 0);

  return (
    <div className={styles.homePage}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Welcome to the Legal500 Staff Platform</h1>
          <p className={styles.subtitle}>Manage your Legal 500 platform content and settings</p>
        </div>
      </div>

      {visibleGroups.map(group => (
        <section key={group.label} className={styles.section}>
          <h2 className={styles.sectionTitle}>{group.label}</h2>
          <div className={styles.tilesGrid}>
            {group.items.map(tile => (
              <Tile key={tile.to} {...tile} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default HomePage;
